import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Mail,
  Megaphone,
  Database,
  Globe,
  Plug,
  Brain,
  ShieldCheck,
  Lock,
  Server,
  Check,
  Sparkles,
  Clock,
  Wallet,
  Bot,
} from "lucide-react";
import {
  Button,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  cn,
} from "@pracownik/ui";
import { PRICING } from "@pracownik/shared/constants";
import { brand, useDocumentTitle } from "@/lib/brand";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/providers/auth-provider";

/* ───────────────────────── Shell ───────────────────────── */

function Nav() {
  const { t } = useTranslation("landing");
  const { isAuthenticated } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#capabilities", label: t("nav.capabilities") },
    { href: "#how", label: t("nav.how") },
    { href: "#pricing", label: t("nav.pricing") },
    { href: "#faq", label: t("nav.faq") },
  ];
  return (
    <header
      className={cn(
        "sticky top-0 z-40 transition-[background-color,border-color,box-shadow] duration-(--dur-base)",
        scrolled
          ? "border-b bg-background/80 backdrop-blur-md shadow-[0_1px_0_0_var(--border)]"
          : "border-b border-transparent",
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <BrandLogo />
        <nav aria-label={t("nav.primary")} className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="rounded-lg px-3 py-2 text-sm text-text-secondary transition-colors hover:bg-accent hover:text-foreground"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle className="hidden sm:inline-flex" />
          {isAuthenticated ? (
            <Button asChild size="sm">
              <Link to="/dashboard">
                {t("nav.dashboard")}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/signup">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Section({
  id,
  eyebrow,
  title,
  lead,
  children,
  className,
  tone = "plain",
}: {
  id?: string;
  eyebrow?: string;
  title?: string;
  lead?: string;
  children: ReactNode;
  className?: string;
  tone?: "plain" | "muted";
}) {
  return (
    <section
      id={id}
      className={cn("scroll-mt-20 py-20 sm:py-28", tone === "muted" && "bg-muted/40", className)}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {(eyebrow || title) && (
          <div className="mx-auto mb-14 max-w-2xl text-center">
            {eyebrow && <p className="text-eyebrow mb-3 text-primary">{eyebrow}</p>}
            {title && <h2 className="text-fluid-h2">{title}</h2>}
            {lead && <p className="mt-4 text-fluid-body-lg text-text-secondary">{lead}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/* ───────────────────────── Hero ───────────────────────── */

function InboxPreview() {
  const { t } = useTranslation("landing");
  const workerName = t("preview.workerName");
  const login = t("preview.workerLogin");
  return (
    <div
      aria-hidden
      className="relative mx-auto w-full max-w-2xl animate-fade-in-up animation-delay-300"
    >
      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-primary/10 blur-3xl" />
      <div className="surface overflow-hidden text-left shadow-elevated">
        <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-2.5">
          <span className="size-2.5 rounded-full bg-border" />
          <span className="size-2.5 rounded-full bg-border" />
          <span className="size-2.5 rounded-full bg-border" />
          <span className="ml-3 truncate font-mono text-[11px] text-text-tertiary">
            {login}@{brand.domain}
          </span>
        </div>
        <div className="grid sm:grid-cols-[1fr_1.4fr]">
          <ul className="hidden divide-y border-r text-[13px] sm:block">
            {[0, 1, 2].map((i) => (
              <li key={i} className={cn("px-4 py-3", i === 0 && "bg-primary-soft/60")}>
                <div className="flex items-center justify-between">
                  <span className="truncate font-medium">{t(`preview.threads.${i}.from`)}</span>
                  <span className="text-[11px] text-text-tertiary">
                    {t(`preview.threads.${i}.time`)}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-text-secondary">
                  {t(`preview.threads.${i}.subject`)}
                </p>
              </li>
            ))}
          </ul>
          <div className="space-y-3 p-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold">
                {t("preview.threads.0.initials")}
              </span>
              <div className="rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-[13px] leading-relaxed">
                {t("preview.inbound")}
              </div>
            </div>
            <div className="flex items-start justify-end gap-3">
              <div className="rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2.5 text-[13px] leading-relaxed text-primary-foreground">
                {t("preview.reply")}
              </div>
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[11px] font-semibold text-primary">
                {workerName.slice(0, 1)}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 text-[11px] text-text-tertiary">
              <Check className="size-3 text-status-success-dot" />
              {t("preview.meta")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const { t } = useTranslation("landing");
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]"
      />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[60vh] bg-glow" />
      <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-16 text-center sm:px-6 sm:pb-28 sm:pt-24">
        <Badge variant="soft" className="mb-6 h-7 px-3 text-[13px] animate-fade-in-up">
          <Sparkles />
          {t("hero.badge")}
        </Badge>
        <h1 className="text-fluid-hero mx-auto max-w-4xl animate-fade-in-up animation-delay-75">
          {t("hero.titleA")} <span className="text-gradient">{t("hero.titleB")}</span>
        </h1>
        <p className="mx-auto mt-6 max-w-[58ch] text-fluid-body-lg text-text-secondary animate-fade-in-up animation-delay-150">
          {t("hero.subtitle")}
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row animate-fade-in-up animation-delay-225">
          <Button asChild size="xl" className="w-full sm:w-auto">
            <Link to="/signup">
              {t("hero.cta")}
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="xl" className="w-full sm:w-auto">
            <a href="#how">{t("hero.secondary")}</a>
          </Button>
        </div>
        <p className="mt-4 text-sm text-text-tertiary animate-fade-in-up animation-delay-300">
          {t("hero.note", {
            days: PRICING.trialDays,
            credits: PRICING.trialCredits.toLocaleString(),
          })}
        </p>
        <div className="mt-16">
          <InboxPreview />
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Capabilities ───────────────────────── */

const CAPABILITIES = [
  { key: "email", icon: Mail },
  { key: "ads", icon: Megaphone },
  { key: "crm", icon: Database },
  { key: "browser", icon: Globe },
  { key: "integrations", icon: Plug },
  { key: "memory", icon: Brain },
] as const;

function Capabilities() {
  const { t } = useTranslation("landing");
  return (
    <Section
      id="capabilities"
      eyebrow={t("capabilities.eyebrow")}
      title={t("capabilities.title")}
      lead={t("capabilities.lead")}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CAPABILITIES.map((c, i) => (
          <div
            key={c.key}
            className="surface card-lift p-6 animate-fade-in-up"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
              <c.icon className="size-5" />
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight">
              {t(`capabilities.items.${c.key}.title`)}
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
              {t(`capabilities.items.${c.key}.body`)}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────── How it works ───────────────────────── */

function HowItWorks() {
  const { t } = useTranslation("landing");
  const steps = ["configure", "knowledge", "connect", "results"] as const;
  return (
    <Section
      id="how"
      tone="muted"
      eyebrow={t("how.eyebrow")}
      title={t("how.title")}
      lead={t("how.lead")}
    >
      <ol className="grid gap-6 md:grid-cols-4">
        {steps.map((s, i) => (
          <li key={s} className="relative">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex size-9 items-center justify-center rounded-full bg-primary font-display text-sm font-semibold text-primary-foreground">
                {i + 1}
              </span>
              {i < steps.length - 1 && (
                <span aria-hidden className="hidden h-px flex-1 bg-border md:block" />
              )}
            </div>
            <h3 className="font-display text-lg font-semibold tracking-tight">
              {t(`how.steps.${s}.title`)}
            </h3>
            <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
              {t(`how.steps.${s}.body`)}
            </p>
          </li>
        ))}
      </ol>
    </Section>
  );
}

/* ───────────────────────── Benefits ───────────────────────── */

function Benefits() {
  const { t } = useTranslation("landing");
  const items = [
    { key: "hire", icon: Clock },
    { key: "budget", icon: Wallet },
    { key: "agent", icon: Bot },
    { key: "data", icon: ShieldCheck },
  ] as const;
  return (
    <Section eyebrow={t("benefits.eyebrow")} title={t("benefits.title")}>
      <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
        {items.map((b) => (
          <div key={b.key} className="flex gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <b.icon className="size-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight">
                {t(`benefits.items.${b.key}.title`)}
              </h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-text-secondary">
                {t(`benefits.items.${b.key}.body`)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ───────────────────────── Security ───────────────────────── */

function Security() {
  const { t } = useTranslation("landing");
  const points = ["isolation", "encryption", "gdpr", "control"] as const;
  const icons = { isolation: Server, encryption: Lock, gdpr: ShieldCheck, control: Globe };
  return (
    <Section tone="muted">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="text-eyebrow mb-3 text-primary">{t("security.eyebrow")}</p>
          <h2 className="text-fluid-h2">{t("security.title")}</h2>
          <p className="mt-4 text-fluid-body-lg text-text-secondary">{t("security.lead")}</p>
        </div>
        <ul className="grid gap-4 sm:grid-cols-2">
          {points.map((p) => {
            const Icon = icons[p];
            return (
              <li key={p} className="surface p-5">
                <Icon className="mb-3 size-5 text-primary" />
                <h3 className="font-medium">{t(`security.points.${p}.title`)}</h3>
                <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                  {t(`security.points.${p}.body`)}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </Section>
  );
}

/* ───────────────────────── Pricing ───────────────────────── */

function Pricing() {
  const { t } = useTranslation("landing");
  const features = ["f1", "f2", "f3", "f4", "f5", "f6"] as const;
  return (
    <Section
      id="pricing"
      eyebrow={t("pricing.eyebrow")}
      title={t("pricing.title")}
      lead={t("pricing.lead")}
    >
      <div className="mx-auto grid max-w-4xl gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="surface relative overflow-hidden p-8 shadow-elevated">
          <div
            aria-hidden
            className="absolute -right-20 -top-20 size-56 rounded-full bg-primary/10 blur-3xl"
          />
          <Badge variant="soft" className="mb-5">
            {t("pricing.planBadge")}
          </Badge>
          <h3 className="font-display text-2xl font-semibold tracking-tight">
            {t("pricing.planName")}
          </h3>
          <p className="mt-4 flex items-baseline gap-1">
            <span className="font-display text-5xl font-semibold tracking-tight tabular">
              ${PRICING.subscription}
            </span>
            <span className="text-text-tertiary">{t("pricing.perMonth")}</span>
          </p>
          <p className="mt-1 text-sm text-text-secondary">{t("pricing.plusTokens")}</p>
          <ul className="mt-6 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-[15px]">
                <Check className="mt-0.5 size-4 shrink-0 text-status-success-dot" />
                <span>
                  {t(`pricing.features.${f}`, { credits: PRICING.monthlyCredits.toLocaleString() })}
                </span>
              </li>
            ))}
          </ul>
          <Button asChild size="xl" className="mt-8 w-full">
            <Link to="/signup">
              {t("pricing.cta")}
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <p className="mt-3 text-center text-xs text-text-tertiary">
            {t("pricing.trial", {
              days: PRICING.trialDays,
              credits: PRICING.trialCredits.toLocaleString(),
            })}
          </p>
        </div>
        <div className="surface-muted p-8">
          <h3 className="font-display text-lg font-semibold tracking-tight">
            {t("pricing.tokens.title")}
          </h3>
          <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
            {t("pricing.tokens.body")}
          </p>
          <dl className="mt-6 space-y-4 text-sm">
            <div className="flex items-center justify-between border-t pt-4">
              <dt className="text-text-secondary">{t("pricing.tokens.rate")}</dt>
              <dd className="font-medium tabular">
                $1 = {PRICING.creditsPerDollar} {t("pricing.tokens.credits")}
              </dd>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <dt className="text-text-secondary">{t("pricing.tokens.email")}</dt>
              <dd className="font-medium tabular">{t("pricing.tokens.emailValue")}</dd>
            </div>
            <div className="flex items-center justify-between border-t pt-4">
              <dt className="text-text-secondary">{t("pricing.tokens.topup")}</dt>
              <dd className="font-medium">{t("pricing.tokens.topupValue")}</dd>
            </div>
          </dl>
        </div>
      </div>
    </Section>
  );
}

/* ───────────────────────── FAQ ───────────────────────── */

function Faq() {
  const { t } = useTranslation("landing");
  const faqs = Array.from({ length: 7 }, (_, i) => ({
    q: t(`faq.q${i + 1}.question`),
    a: t(`faq.q${i + 1}.answer`),
  }));
  return (
    <Section id="faq" tone="muted" eyebrow={t("faq.eyebrow")} title={t("faq.title")}>
      <div className="mx-auto max-w-2xl">
        <Accordion type="single" collapsible className="surface divide-y px-6">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-0 border-b last:border-b-0">
              <AccordionTrigger className="py-5 text-left text-[15px] font-medium hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-[15px] leading-relaxed text-text-secondary">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}

/* ───────────────────────── CTA + footer ───────────────────────── */

function FinalCta() {
  const { t } = useTranslation("landing");
  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-primary px-6 py-16 text-center text-primary-foreground sm:px-16">
          <div aria-hidden className="absolute inset-0 bg-dots opacity-20" />
          <div
            aria-hidden
            className="absolute -left-20 top-0 size-72 rounded-full bg-white/10 blur-3xl"
          />
          <div className="relative">
            <h2 className="text-fluid-h2">{t("cta.title")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-fluid-body-lg opacity-85">
              {t("cta.subtitle")}
            </p>
            <Button
              asChild
              size="xl"
              variant="secondary"
              className="mt-8 bg-white text-primary hover:bg-white/90"
            >
              <Link to="/signup">
                {t("cta.button")}
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation("landing");
  return (
    <footer className="border-t py-12">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:grid-cols-[1.5fr_1fr_1fr_1fr] sm:px-6">
        <div>
          <BrandLogo />
          <p className="mt-3 max-w-xs text-sm text-text-secondary">{t("footer.tagline")}</p>
          <p className="mt-4 text-xs text-text-tertiary">
            {brand.company} · Twarda 18, 00-105 Warszawa
          </p>
        </div>
        <div>
          <h4 className="text-eyebrow mb-3 text-text-tertiary">{t("footer.product")}</h4>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li>
              <a href="#capabilities" className="hover:text-foreground">
                {t("nav.capabilities")}
              </a>
            </li>
            <li>
              <a href="#pricing" className="hover:text-foreground">
                {t("nav.pricing")}
              </a>
            </li>
            <li>
              <Link to="/signup" className="hover:text-foreground">
                {t("footer.getStarted")}
              </Link>
            </li>
            <li>
              <Link to="/login" className="hover:text-foreground">
                {t("nav.login")}
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-eyebrow mb-3 text-text-tertiary">{t("footer.company")}</h4>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li>
              <a href={brand.landingUrl} className="hover:text-foreground">
                {brand.name}
              </a>
            </li>
            <li>
              <a href={`mailto:${brand.supportEmail}`} className="hover:text-foreground">
                {t("footer.contact")}
              </a>
            </li>
            <li>
              <a href="https://wondel.ai" className="hover:text-foreground">
                Wondel.ai
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-eyebrow mb-3 text-text-tertiary">{t("footer.legal")}</h4>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li>
              <Link to="/terms" className="hover:text-foreground">
                {t("footer.terms")}
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-foreground">
                {t("footer.privacy")}
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="mx-auto mt-10 flex max-w-6xl flex-wrap items-center justify-between gap-3 border-t px-4 pt-6 text-xs text-text-tertiary sm:px-6">
        <span>{t("footer.copyright", { year: new Date().getFullYear(), brand: brand.name })}</span>
        <span>{t("footer.poweredBy")}</span>
      </div>
    </footer>
  );
}

export function LandingPage() {
  const { t } = useTranslation("landing");
  useDocumentTitle(t("meta.title"));
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("meta.description"));
  }, [t]);
  return (
    <div className="min-h-dvh bg-background">
      <a href="#main" className="skip-link">
        {t("nav.skipToContent")}
      </a>
      <Nav />
      <main id="main">
        <Hero />
        <Capabilities />
        <HowItWorks />
        <Benefits />
        <Security />
        <Pricing />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
